'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { Banner } from '@flipster/apis/v1/banner/types';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import ArrowLeftIcon from '@flipster/react/icon/generated/ArrowLeftIcon';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import XMarkIcon from '@flipster/react/icon/generated/XMarkIcon';
import { css, cx } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { useCheckSpotOnly } from '../shared/regulation';
import { clearStaleInlineBannerDismiss, dismissInlineBanner, isInlineBannerDismissed } from './dismiss';
import useBanners from './use-banners';

export type InlinePromoBannerVariant = 'MARKET' | 'TRADFI_ORDER_FORM';

export interface InlinePromoBannerClassNames {
  root?: string;
  card?: string;
  title?: string;
  description?: string;
}

interface InlinePromoBannerProps {
  variant: InlinePromoBannerVariant;
  enabled?: boolean;
  onView?: (banner: Banner) => void;
  onClickCta?: (banner: Banner) => void;
  classNames?: InlinePromoBannerClassNames;
}

const marketAccentTokens = [
  'colors.content.accent.promotion',
  'colors.content.accent.info',
  'colors.content.state.positive',
] as const;

const autoRotateMs = 3000;

export const InlinePromoBanner = (props: InlinePromoBannerProps) => {
  const isSpotOnly = useCheckSpotOnly();
  const { banners } = useBanners({ inline: [props.variant] });
  const sectionBanners = banners?.inline?.[props.variant] ?? [];
  const enabled = (props.enabled ?? true) && !isSpotOnly;

  return props.variant === 'MARKET' ? (
    <MarketBannerCarousel {...props} enabled={enabled} banners={sectionBanners} />
  ) : (
    <OrderFormBanner {...props} enabled={enabled} banner={sectionBanners[0]} />
  );
};

interface MarketBannerCarouselProps extends InlinePromoBannerProps {
  enabled: boolean;
  banners: Banner[];
}

const MarketBannerCarousel = ({ banners, enabled, onView, onClickCta, classNames }: MarketBannerCarouselProps) => {
  const [closedIds, setClosedIds] = useState<Set<number>>(() => new Set());
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const visibleBanners = useMemo(
    () => banners.filter((banner) => !closedIds.has(banner.id) && !isInlineBannerDismissed('MARKET', banner.id)),
    [banners, closedIds],
  );
  const count = visibleBanners.length;
  const activeIndex = Math.min(index, Math.max(count - 1, 0));
  const banner = visibleBanners[activeIndex];

  useEffect(() => {
    clearStaleInlineBannerDismiss(
      'MARKET',
      banners.map((b) => b.id),
    );
  }, [banners]);

  const canRotate = enabled && !isPaused && count >= 2;
  useEffect(() => {
    if (!canRotate) return;
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % count), autoRotateMs);
    return () => clearInterval(timer);
  }, [canRotate, count]);

  useBannerView(banner, enabled, onView);

  if (!enabled || !banner) return null;

  const accentColor = token(marketAccentTokens[activeIndex % marketAccentTokens.length]);
  const showControls = count >= 2;

  const nav = (
    <CarouselNav
      current={activeIndex}
      count={count}
      onPrev={() => setIndex((prev) => Math.max(0, prev - 1))}
      onNext={() => setIndex((prev) => (prev + 1) % count)}
    />
  );

  const cardBody = (
    <>
      {banner.imagePath && <BannerIcon src={banner.imagePath} alt={banner.title} />}
      <BannerText banner={banner} titleColor={accentColor} classNames={classNames} />
      {!banner.closable && banner.webLink && (
        <p className={css({ display: 'flex', flexShrink: 0 })}>
          <ChevronRightIcon size={16} color={token('colors.content.default.level-3')} />
        </p>
      )}
    </>
  );
  const cardBodyClassName = hstack({ gap: 20, minW: 0, flexGrow: { base: 1, tablet: 0 } });

  return (
    <div
      className={cx(css({ position: 'relative' }), classNames?.root)}
      onPointerEnter={(e) => e.pointerType === 'mouse' && setIsPaused(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      <div
        className={cx(classNames?.card, css({ position: 'relative' }))}
        style={{ backgroundColor: `color-mix(in srgb, ${accentColor} 6%, transparent)` }}
      >
        {banner.webLink ? (
          <IsomorphicLink
            href={banner.webLink}
            external
            onClick={() => onClickCta?.(banner)}
            className={cx(
              cardBodyClassName,
              css({ cursor: 'pointer', _after: { content: '""', position: 'absolute', inset: 0 } }),
            )}
          >
            {cardBody}
          </IsomorphicLink>
        ) : (
          <div className={cardBodyClassName}>{cardBody}</div>
        )}
        {showControls && (
          <div
            className={css({
              display: { base: 'none', tablet: 'flex' },
              flex: 1,
              justifyContent: 'flex-end',
              position: 'relative',
              zIndex: 1,
              pointerEvents: 'none',
            })}
          >
            {nav}
          </div>
        )}
        {banner.closable && (
          <CloseButton
            offset={12}
            onClick={() => {
              dismissInlineBanner('MARKET', banner.id);
              setClosedIds((prev) => new Set(prev).add(banner.id));
            }}
          />
        )}
      </div>
      {showControls && <div className={css({ display: { base: 'flex', tablet: 'none' }, mt: 8, px: 4 })}>{nav}</div>}
    </div>
  );
};

interface OrderFormBannerProps extends InlinePromoBannerProps {
  enabled: boolean;
  banner: Banner | undefined;
}

const OrderFormBanner = ({ banner, enabled, onView, onClickCta, classNames }: OrderFormBannerProps) => {
  const [isClosed, setIsClosed] = useState(false);
  useBannerView(banner, enabled, onView);

  if (!enabled || !banner || isClosed || isInlineBannerDismissed('TRADFI_ORDER_FORM', banner.id)) return null;

  return (
    <div className={classNames?.root}>
      <div
        className={cx(
          vstack({
            position: 'relative',
            alignItems: 'stretch',
            gap: 8,
            px: 16,
            py: 24,
            rounded: 12,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.accent.promotion',
            bgColor: 'background.accent.promotion.tint',
          }),
          classNames?.card,
        )}
      >
        <div className={hstack({ gap: 12, alignItems: 'center', w: 'full' })}>
          {banner.imagePath && <BannerIcon src={banner.imagePath} alt={banner.title} />}
          <BannerText banner={banner} classNames={classNames} compact />
        </div>
        {banner.webLink && (
          <Button
            as='a'
            type='rounded'
            size='small'
            href={banner.webLink}
            external
            onClick={() => onClickCta?.(banner)}
            label={banner.buttonText?.trim() || <FormattedMessage id='Common.r250401.Learn_more' />}
            css={{
              alignSelf: 'flex-end',
              textStyle: 'Body/XS/Strong',
              color: 'inverse.black',
              bgColor: 'content.accent.promotion',
              _hover: { bg: 'content.accent.promotion.hover' },
              rounded: 'full',
              whiteSpace: 'nowrap',
            }}
          />
        )}
        {banner.closable && (
          <CloseButton
            offset={8}
            onClick={() => {
              dismissInlineBanner('TRADFI_ORDER_FORM', banner.id);
              setIsClosed(true);
            }}
          />
        )}
      </div>
    </div>
  );
};

const useBannerView = (banner: Banner | undefined, enabled: boolean, onView?: (banner: Banner) => void) => {
  const onViewRef = useRef(onView);
  onViewRef.current = onView;
  const viewedSegmentIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!enabled || !banner || viewedSegmentIdRef.current === banner.segmentId) return;
    viewedSegmentIdRef.current = banner.segmentId;
    onViewRef.current?.(banner);
  }, [enabled, banner]);
};

const BannerIcon = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} className={css({ rounded: 'full', flexShrink: 0, objectFit: 'cover', w: 32, h: 32 })} />
);

interface BannerTextProps {
  banner: Banner;
  titleColor?: string;
  classNames?: InlinePromoBannerClassNames;
  compact?: boolean;
}

const BannerText = ({ banner, titleColor, classNames, compact }: BannerTextProps) => (
  <div
    className={vstack({
      gap: compact ? 4 : 2,
      alignItems: 'flex-start',
      flex: compact ? 1 : { base: '1', tablet: '0 1 auto' },
      minW: 0,
    })}
  >
    <p
      className={cx(
        css({ textStyle: compact ? 'Body/S/Strong' : 'Body/M/Strong', w: 'full', lineClamp: 1 }),
        classNames?.title,
      )}
      style={{ color: titleColor ?? token('colors.content.accent.promotion') }}
    >
      {banner.primaryText}
    </p>
    {banner.secondaryText && (
      <p
        className={cx(
          css({
            textStyle: compact ? 'Body/XS/Regular' : 'Body/S/Regular',
            color: 'content.default.level-4',
            w: 'full',
            lineClamp: 1,
          }),
          classNames?.description,
        )}
      >
        {banner.secondaryText}
      </p>
    )}
  </div>
);

interface CarouselNavProps {
  current: number;
  count: number;
  onPrev: () => void;
  onNext: () => void;
}

const CarouselNav = ({ current, count, onPrev, onNext }: CarouselNavProps) => {
  const isFirst = current === 0;
  return (
    <div className={hstack({ gap: 8, flexShrink: 0, pointerEvents: 'auto', cursor: 'pointer' })}>
      <button
        type='button'
        aria-label='previous banner'
        disabled={isFirst}
        onClick={onPrev}
        className={css({ _disabled: { cursor: 'default' } })}
      >
        <ArrowLeftIcon
          size={16}
          color={token(isFirst ? 'colors.content.default.level-1' : 'colors.content.default.level-3')}
        />
      </button>
      <p
        className={css({
          textStyle: 'Body/XS/Monospace/Regular',
          color: 'content.default.level-3',
          minW: 32,
          textAlign: 'center',
        })}
      >
        {current + 1}/{count}
      </p>
      <button type='button' aria-label='next banner' onClick={onNext} className={css({ cursor: 'pointer' })}>
        <ArrowRightIcon size={16} color={token('colors.content.default.level-3')} />
      </button>
    </div>
  );
};

const CloseButton = ({ offset, onClick }: { offset: number; onClick: () => void }) => (
  <button
    type='button'
    aria-label='close'
    onClick={onClick}
    className={css({ position: 'absolute', cursor: 'pointer', zIndex: 1 })}
    style={{ top: offset, right: offset }}
  >
    <XMarkIcon size={16} color={token('colors.content.default.level-2')} />
  </button>
);
